/**
 * Government travel advisory and security alert data source.
 * Aggregates 27 RSS/Atom feeds from government agencies worldwide:
 *   - Travel advisories (US State Dept, UK FCDO, Australia, Canada, etc.)
 *   - Health alerts (WHO, CDC, ECDC)
 *   - Security alerts (NCTC, INTERPOL)
 *   - Maritime/aviation security (UKMTO, FAA NOTAMs)
 *
 * Reuses the existing RSS proxy (api/rss/feed.ts) — no dedicated API proxy.
 * Feeds are fetched via Promise.allSettled for resilience.
 */

import type { Source, SourceMeta, ConfigField, SourceStatus, NormalizedEvent, Severity } from '../base-source';
import { ADVISORY_FEEDS, type AdvisoryFeedDef, type AdvisoryCategory } from '@/config/security-advisories';
import { stripHtml } from '@/utils/sanitize';
import { djb2Hash } from '@/utils/hash';
import { getApiBase } from '@/app/api-router';

const SOURCE_ID = 'advisory';

/** Max feeds to fetch per cycle (avoid overwhelming the RSS proxy). */
const MAX_FEEDS_PER_CYCLE = 15;

/** Rotate which feeds are fetched each cycle. */
let feedCycleOffset = 0;

function categoryToSeverity(category: AdvisoryCategory): Severity {
  switch (category) {
    case 'security': return 'high';
    case 'maritime':
    case 'aviation': return 'medium';
    case 'health': return 'medium';
    case 'travel': return 'low';
    default: return 'info';
  }
}

function categoryToEventType(category: AdvisoryCategory): NormalizedEvent['type'] {
  switch (category) {
    case 'security': return 'political';
    case 'maritime': return 'infrastructure';
    case 'aviation': return 'infrastructure';
    case 'health': return 'disaster';
    case 'travel': return 'political';
    default: return 'political';
  }
}

/** Parse a single RSS/Atom feed and extract items. */
function parseRssFeed(xml: string, feedDef: AdvisoryFeedDef): NormalizedEvent[] {
  const parser = new DOMParser();
  const doc = parser.parseFromString(xml, 'text/xml');

  if (doc.querySelector('parsererror')) {
    return [];
  }

  const events: NormalizedEvent[] = [];

  // Try RSS 2.0 items first, then Atom entries
  let items = doc.querySelectorAll('item');
  const isAtom = items.length === 0;
  if (isAtom) {
    items = doc.querySelectorAll('entry');
  }

  for (const item of items) {
    let title: string;
    let description: string;
    let link: string;
    let pubDate: string;

    if (isAtom) {
      title = item.querySelector('title')?.textContent?.trim() ?? '';
      description = item.querySelector('summary')?.textContent ??
                     item.querySelector('content')?.textContent ?? '';
      link = item.querySelector('link')?.getAttribute('href') ??
             item.querySelector('link')?.textContent?.trim() ?? '';
      pubDate = item.querySelector('published')?.textContent?.trim() ??
                item.querySelector('updated')?.textContent?.trim() ?? '';
    } else {
      title = item.querySelector('title')?.textContent?.trim() ?? '';
      description = item.querySelector('description')?.textContent ?? '';
      link = item.querySelector('link')?.textContent?.trim() ?? '';
      pubDate = item.querySelector('pubDate')?.textContent?.trim() ?? '';
    }

    if (!title) continue;

    let timestamp: string;
    try {
      timestamp = pubDate ? new Date(pubDate).toISOString() : new Date().toISOString();
    } catch {
      timestamp = new Date().toISOString();
    }

    const id = `advisory-${feedDef.id}-${djb2Hash(link || title)}`;

    events.push({
      id,
      source: SOURCE_ID,
      timestamp,
      type: categoryToEventType(feedDef.category),
      title: `[${feedDef.country}] ${title}`,
      description: stripHtml(description).slice(0, 500) || undefined,
      severity: categoryToSeverity(feedDef.category),
      tags: [
        'advisory',
        feedDef.category,
        feedDef.countryCode.toLowerCase(),
        feedDef.id,
      ],
      links: link ? [{ url: link, label: feedDef.name }] : [],
      raw: {
        feedId: feedDef.id,
        feedName: feedDef.name,
        country: feedDef.country,
        countryCode: feedDef.countryCode,
        category: feedDef.category,
        language: feedDef.language,
      },
    });
  }

  return events;
}

let status: SourceStatus = {
  state: 'initializing',
  eventCount: 0,
};

export const advisorySource: Source = {
  id: SOURCE_ID,

  meta: {
    name: 'Travel Advisories',
    description: 'Government travel and security advisory aggregation (27 feeds)',
    icon: 'flag',
    category: 'government',
    updateInterval: 3_600_000, // 1 hour
    requiresApiKey: false,
    dataLicense: 'Public Domain / Government',
    builtIn: true,
  } satisfies SourceMeta,

  configSchema: [] as ConfigField[],

  async initialize(): Promise<void> {
    status = { state: 'initializing', eventCount: 0 };
  },

  async start(): Promise<void> {
    status.state = 'active';
    console.log('[Sentinel] Advisory feed source started');
  },

  async stop(): Promise<void> {
    status.state = 'disabled';
  },

  async fetch(): Promise<NormalizedEvent[]> {
    try {
      // Select a slice of feeds to fetch this cycle (round-robin)
      const feedSlice: AdvisoryFeedDef[] = [];
      for (let i = 0; i < MAX_FEEDS_PER_CYCLE && i < ADVISORY_FEEDS.length; i++) {
        const idx = (feedCycleOffset + i) % ADVISORY_FEEDS.length;
        feedSlice.push(ADVISORY_FEEDS[idx]);
      }
      feedCycleOffset = (feedCycleOffset + MAX_FEEDS_PER_CYCLE) % ADVISORY_FEEDS.length;

      const base = getApiBase();

      // Fetch all feeds in parallel via the RSS proxy
      const results = await Promise.allSettled(
        feedSlice.map(async (feedDef) => {
          const proxyUrl = `${base}/api/rss/feed?url=${encodeURIComponent(feedDef.url)}`;
          const response = await fetch(proxyUrl, { signal: AbortSignal.timeout(10_000) });

          if (!response.ok) {
            throw new Error(`RSS proxy returned ${response.status} for ${feedDef.id}`);
          }

          const xml = await response.text();
          return parseRssFeed(xml, feedDef);
        }),
      );

      const events: NormalizedEvent[] = [];
      let feedErrors = 0;

      for (const result of results) {
        if (result.status === 'fulfilled') {
          events.push(...result.value);
        } else {
          feedErrors++;
        }
      }

      if (feedErrors > 0) {
        console.warn(`[Sentinel] Advisory: ${feedErrors}/${feedSlice.length} feeds failed`);
      }

      status.state = 'active';
      status.lastFetch = new Date().toISOString();
      status.eventCount = events.length;
      return events;
    } catch (err) {
      status.state = 'error';
      status.lastError = err instanceof Error ? err.message : String(err);
      throw err;
    }
  },

  getStatus(): SourceStatus {
    return { ...status };
  },
};
